import { useMessageStore } from "@/store/messages";
import { cn } from "@/utils/cn";
import { MsgList } from "@/components/messages/MsgList";
import { MsgDetails } from "@/components/messages/MsgDetails";
import { NewMessageForm } from "@/components/messages/NewMessageForm";

export function MessagesPage() {
  const currentMsgPageView = useMessageStore((s) => s.currentMsgPageView);
  const currentMsgInfo = useMessageStore((s) => s.currentMsgInfo);
  const createMessageOpen = useMessageStore((s) => s.createMessageOpen);
  const setCreateMessageOpen = useMessageStore(
    (s) => s.setCreateMessageOpen
  );
  const setMsgCurrentPage = useMessageStore((s) => s.setMsgCurrentPage);
  const setCurrentMsgInfo = useMessageStore((s) => s.setCurrentMsgInfo);

  const showThread = currentMsgPageView === "MsgDetails" && currentMsgInfo?.id != null;
  const subject = (currentMsgInfo?.subject as string) ?? "";

  const backToList = () => {
    setCurrentMsgInfo(null);
    setMsgCurrentPage("MsgList");
  };

  return (
    <div
      className={cn(
        "flex min-h-0 w-full min-w-0 max-w-full flex-col overflow-x-clip overflow-y-hidden sm:bg-white sm:min-h-[min(22rem,52dvh)] sm:flex-1 sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)] min-[900px]:min-h-[24rem] md:min-h-0 md:flex-row",
        // Below sm the list sits flat on the page at its natural height; an open thread still fills the screen.
        showThread && "max-sm:min-h-[min(22rem,52dvh)] max-sm:flex-1 max-sm:bg-white",
      )}
    >
      {/* Single message list: full width on mobile; fixed sidebar on md+ */}
      <aside
        className={cn(
          "flex min-h-0 w-full min-w-0 max-w-full flex-col border-healthsource-lightLavender py-2.5 max-sm:!px-0 xs:py-3 pl-[max(0.3rem,env(safe-area-inset-left,0px))] pr-[max(0.3rem,env(safe-area-inset-right,0px))] 2xs:pl-2.5 2xs:pr-2.5 md:min-w-[10rem] md:max-w-[min(16rem,32vw)] md:shrink-0 md:border-r md:py-3 md:pl-2.5 md:pr-2.5",
          showThread && "max-md:hidden",
        )}
      >
        {/* Same section heading as Appointments/Profile below sm; inside the sm+ card the accent bar is dropped. */}
        <div className="flex items-center px-2 pb-3 pt-1 max-sm:px-0">
          <div
            className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy sm:hidden"
          />
          <h1 className="text-xl font-extrabold text-healthsource-dark">
            Messages
          </h1>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto sm:min-h-[min(10rem,32dvh)] md:min-h-0">
          <MsgList variant="panel" />
        </div>
      </aside>

      <section
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          // Below md, hide the right column until a thread is open (or desktop compose, below).
          !showThread && "max-md:hidden",
        )}
      >
        {createMessageOpen ? (
          <div className="hidden min-h-0 min-h-[min(18rem,50dvh)] flex-1 flex-col overflow-y-auto p-3 xs:p-4 md:min-h-0 min-[800px]:min-h-[20rem] md:flex">
            <NewMessageForm
              onCancel={() => setCreateMessageOpen(false)}
            />
          </div>
        ) : !showThread ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-3 py-6 text-healthsource-gray xs:px-4 xs:py-8">
            <p className="text-sm font-semibold">Select a message</p>
            <p className="text-xs">Choose a conversation from the list</p>
          </div>
        ) : (
          <div className="flex h-full min-h-0 min-h-[min(18rem,50dvh)] flex-1 flex-col xs:min-h-[min(20rem,52dvh)] md:min-h-0">
            {/* Mobile-only: back + title */}
            <div className="flex h-11 min-h-11 shrink-0 items-center gap-0.5 border-b border-healthsource-lightLavender bg-white pl-0 pr-1 xs:h-12 xs:min-h-12 xs:pr-2 md:hidden">
              <button
                type="button"
                onClick={backToList}
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-healthsource-dark hover:bg-healthsource-lightGray/80"
                aria-label="Back to message list"
              >
                <i className="fas fa-chevron-left text-lg" aria-hidden />
              </button>
              <h2 className="min-w-0 flex-1 truncate pr-1 text-base font-extrabold text-healthsource-dark">
                {subject || "Message"}
              </h2>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <MsgDetails hideBack inlineReply />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
